<template>
    <section :class="['home-page', { 'home-page--empty': !contentLoaded }]">
        <template v-if="contentLoaded">
            <HeroBanner v-bind="heroBanner" />
            <CustomHtmlBlock
                v-if="customHeadHtml"
                :content="customHeadHtml"
            />
            <ShoppingSundaysInfo :bem-modifiers="['homepage','container']" />
            <CategoriesDropDownList class="desktop-hide tablet-hide" />
            <IndependentContentWrapper>
                <RowBanner v-bind="rowBanner" />
                <SquareBanners :list="squareBanner" />
            </IndependentContentWrapper>
            <IndependentContentWrapper
                :title="category.title"
                :bem-modifiers="['white']"
            >
                <CategoriesList :list="category.collection" />
            </IndependentContentWrapper>
            <IndependentContentWrapper :title="inspiration.title">
                <InspirationsList :list="inspiration.collection" />
            </IndependentContentWrapper>
            <IndependentContentWrapper
                title="Zakupy w Castorama"
                subtitle="Dzięki Castorama tak łatwo budujesz remontujesz i urządzasz!"
                class="mobile-hide"
                :bem-modifiers="['blue']"
            >
                <ShoppingPictogram />
            </IndependentContentWrapper>
            <IndependentContentWrapper :bem-modifiers="['white-mobile']">
                <MiniHeroBanner
                    :placeholders="miniHeroBanner"
                    class="mini-hero-banner"
                    :progressive-image="true"
                />
                <ArticleBlock
                    v-if="article"
                    :description="article.description"
                    :short-description="article.shortDescription"
                    :title="article.title"
                />
            </IndependentContentWrapper>
            <NewsletterFold class="mobile-hide" />
            <client-only>
                <RecentlyProducts />
                <RecentlyViewedCategories />
            </client-only>
            <CustomHtmlBlock
                v-if="customHtml"
                :content="customHtml"
            />
        </template>
    </section>
</template>

<script>
import { mapState, createNamespacedHelpers } from 'vuex';
import ShoppingSundaysInfo from '~/src/components/ShoppingSundaysInfo';
import { getData, config } from '~/assets/services/homepage';
import RowBanner from '~/components/HomePage/RowBanner';
import CategoriesList from '~/components/HomePage/CategoriesList';
import InspirationsList from '~/components/HomePage/InspirationsList';
import IndependentContentWrapper from '~/components/HomePage/Wrapper/IndependentContentWrapper';
import ShoppingPictogram from '~/components/HomePage/ShoppingPictogram';
import ArticleBlock from '~/components/HomePage/ArticleBlock';
import NewsletterFold from '~/components/HomePage/NewsletterFold';
import RecentlyProducts from '~/components/RecentlyProducts';
import RecentlyViewedCategories from '~/components/common/Category/RecentlyViewedCategories';
import CategoriesDropDownList from '~/components/HomePage/CategoriesDropDownList';
import CustomHtmlBlock from '~/components/HomePage/Wrapper/CustomHtmlBlock';
import SquareBanners from '~/components/HomePage/SquareBanners';
import HeroBanner from '~/components/HomePage/HeroBanner';
import MiniHeroBanner from '~/components/common/MiniHeroBanner';
import pageWithDoubleClickSlots from '~/mixins/page/pageWithDoubleClickSlots';
import watchOnce from '~/mixins/watchOnce';
import miniHeroBannerSlots from '~/src/config/doubleclick/miniHeroBanner';

const { mapState: mapStateGoogleServices } = createNamespacedHelpers('googleServices');

const { title } = config;

export default {
    layout: 'simple',
    name: 'HomePage',
    components: {
        ShoppingSundaysInfo,
        MiniHeroBanner,
        HeroBanner,
        SquareBanners,
        CustomHtmlBlock,
        CategoriesDropDownList,
        RecentlyViewedCategories,
        RecentlyProducts,
        NewsletterFold,
        ArticleBlock,
        ShoppingPictogram,
        IndependentContentWrapper,
        InspirationsList,
        CategoriesList,
        RowBanner,
    },
    mixins: [pageWithDoubleClickSlots, watchOnce],
    async asyncData() {
        try {
            return await getData();
        } catch {
            return {
                apiError: true,
            };
        }
    },
    data() {
        return {
            apiError: false,
            category: null,
            inspiration: null,
            miniHeroBanner: null,
            squareBanner: null,
            rowBanner: null,
            heroBanner: null,
            article: null,
            customHtml: null,
            doubleClickSlots: [...miniHeroBannerSlots],
            customHeadHtml: null,
        };
    },
    computed: {
        contentLoaded() {
            const { apiError, category, inspiration, miniHeroBanner, squareBanner, rowBanner, heroBanner, article } = this;
            return !apiError || (category && inspiration && miniHeroBanner && squareBanner && rowBanner && heroBanner && article);
        },
        ...mapState({
            adBlockDetected: state => state.adblock,
        }),
        ...mapStateGoogleServices({
            gptLoaded: state => state.GPT,
        }),
    },
    head() {
        return {
            title,
        };
    },
};
</script>

<style lang="scss">
    .home-page {
        .row-banner {
            margin-top: -22px;
        }

        &--empty {
            min-height: 400px;
        }

        .mini-hero-banner {
            display: flex;
            width: calc(100% + 20px);
            padding: 5px 0 55px 0;
            margin: 0 -10px;
            flex-wrap: wrap;
            line-height: 0;

            &__image-wrapper {
                flex: 0 1 calc(50% - 20px);
                margin: 0 10px;
                text-align: center;
            }

            img#{&}__image {
                display: inline;
                max-width: 100%;
            }

            .single-mini-hero-banner__item {
                background: $color-slate5;
            }
        }
    }

    @media (max-width: $breakpoint-small-desktop) {
        .home-page {
            .mini-hero-banner {
                overflow: hidden;

                &__image-wrapper {
                    flex: 0 0 100%;
                    margin: 0;
                }

                &__image-wrapper + &__image-wrapper {
                    margin-top: 22px;
                }

                .single-mini-hero-banner__item {
                    background: none;
                }

                &__slot--left, &__slot--right {
                    padding: 0;
                }

                &__slot--right {
                    margin-top: 22px;
                }
            }
        }
    }

    @media (max-width: $breakpoint-tablet) {
        .home-page {
            .row-banner {
                margin-top: -5px;
                margin-bottom: -5px;
            }

            .mini-hero-banner {
                padding-bottom: 46px;

                &__image-wrapper + &__image-wrapper {
                    margin-top: 0;
                }
            }
        }
    }
</style>
